import { CardContent, Card, Dialog, DialogContent, DialogTitle, Grid2, Typography, Container, Box, CardHeader, Button, IconButton, useMediaQuery, ButtonPropsColorOverrides } from '@mui/material';
import React from 'react';
import { isIntroDialogOpenSetState, selectIsIntroDialogOpen } from '../containers/authSlice';
import { useAppDispatch, useAppSelector } from '../hooks';
import { localStorageSetIsIntroDialogCompleted } from '../localStorage';
import { Link, useNavigate } from 'react-router-dom';
import NoteIcon from '@mui/icons-material/Note';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactGA from 'react-ga4';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { useThemeContext } from '../ThemeProvider';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import { Theme } from '../types';
import { LandingDemo, requestLandingDemoImport } from '../hooks/useOnboardingFlow';
import { entriesSetState, selectAllEntries } from '../containers/contentSlice';

const IntroDialog = () => {
	const dispatch = useAppDispatch();
	const isIntroDialogOpen = useAppSelector(selectIsIntroDialogOpen);
	const allEntries = useAppSelector(selectAllEntries);
	const hasEntries = allEntries.length > 0;
	const navigate = useNavigate();
	const { analyticsConsent, cookiesAllowIntroContinue, requestCookiePromptFocus } = useCookieConsent();
	const { isDarkModeOn } = useThemeContext();
	const handleDemoClick = (demo: LandingDemo, analyticsAction: string) => {
		localStorageSetIsIntroDialogCompleted(true);
		requestLandingDemoImport(demo);
		dispatch(isIntroDialogOpenSetState(false));
		if (analyticsConsent) {
			ReactGA.event({
				category: 'UI',
				action: analyticsAction,
			});
		}
	};

	const handleJournalingClick = () => {
		handleDemoClick('journal', 'IntroJournalingClicked');
	};

	const handleTeamPerformanceClick = () => {
		handleDemoClick('engineering', 'IntroTeamPerformanceClicked');
	};

	const handleRoadmapPlanningClick = () => {
		handleDemoClick('roadmap', 'IntroRoadmapPlanningClicked');
	};

	const handleGoalTrackingClick = () => {
		handleDemoClick('bracelets', 'IntroGoalTrackingClicked');
	};

	const handleClearWorkspaceClick = () => {
		localStorageSetIsIntroDialogCompleted(true);
		dispatch(entriesSetState([]));
		dispatch(isIntroDialogOpenSetState(false));
		navigate('/');
		if (analyticsConsent) {
			ReactGA.event({
				category: 'UI',
				action: 'IntroClearLocalWorkspaceClicked',
			});
		}
	};

	const handleCloseDialog = () => {
		localStorageSetIsIntroDialogCompleted(true);
		dispatch(isIntroDialogOpenSetState(false));
	};

	const presetCardSx = {
		height: '100%',
		width: '100%',
		display: 'block',
		textAlign: 'left' as const,
		transition: 'box-shadow 0.2s, transform 0.15s',
		cursor: 'pointer',
		boxShadow: 2,
		'&:hover:not(:disabled)': {
			boxShadow: 4,
		},
		'&:disabled': {
			opacity: 0.5,
		},
	};

	const options: {
		icon: React.ReactNode;
		title: string;
		description: string;
		onClick: () => void;
		isDisabled: boolean;
		color?: ButtonPropsColorOverrides;
		actionColor?: ButtonPropsColorOverrides;
		actionLabel?: string;
	}[] = [
		{
			icon: <MapIcon color='secondary' />,
			title: 'Roadmap planning',
			description: 'Define, group, and prioritize your backlog in one visual interface. Build roadmaps that make sense for your team — and your clients actually get excited about.',
			onClick: handleRoadmapPlanningClick,
			isDisabled: false,
			color: 'secondary.main',
		},
		{
			icon: <TrackChangesIcon color='success' />,
			title: 'Goal tracking',
			description: 'Define goals, log progress over time. Simple.',
			onClick: handleGoalTrackingClick,
			isDisabled: false,
			color: 'success.main',
		},
		{
			icon: <TimelineIcon color='warning' />,
			title: 'Tracking team performance',
			description: 'Gather engineering metrics, backlog health signals, and charts on one canvas so leads can adjust focus before surprises land.',
			onClick: handleTeamPerformanceClick,
			isDisabled: false,
			color: 'primary.main',
		},
		{
			icon: <NoteIcon color='info' />,
			title: 'Daily journaling',
			description: 'Journaling your goals daily can be quite an impactful habit.',
			onClick: handleJournalingClick,
			isDisabled: false,
			color: 'info.main',
		},
		...(hasEntries ? [{
			icon: <DeleteIcon color='error' />,
			title: 'Clear workspace',
			description: 'Reset the contents of your offline workspace.',
			onClick: handleClearWorkspaceClick,
			isDisabled: false,
			color: 'error.main',
			actionColor: 'error',
			actionLabel: 'Clear offline workspace',
		}] : []),
	];

	const [selectedOption, setSelectedOption] = React.useState<string | null>(options[0].title);

	const selectableOptions = options.filter((option) => !option.isDisabled);
	const selectedOptionConfig = selectableOptions.find((item) => item.title === selectedOption);

	const handleContinue = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!selectedOption || !cookiesAllowIntroContinue) return;

		if (!selectedOptionConfig) return;
		selectedOptionConfig.onClick();
	};

	const needsCookieChoice = !cookiesAllowIntroContinue;
	const canSubmitContinue = Boolean(selectedOption && !needsCookieChoice);

	const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	return <Dialog open={isIntroDialogOpen} fullWidth maxWidth='sm' scroll='body' fullScreen={!isMdUp ? true : false}>
		<IconButton
			aria-label='Close intro dialog'
			onClick={handleCloseDialog}
			sx={{
				position: 'absolute',
				right: 16,
				top: 16,
				zIndex: 1,
			}}
		>
			<CloseIcon />
		</IconButton>
		<Box pt={10} pb={10}>
			<Box display='flex' flexDirection='column' alignItems='center' textAlign='center' gap={1}>
				{isDarkModeOn ? <img src='/logo-feather-dark.svg' height='64' alt='Untwyn logo' /> : <img src='/logo-feather.svg' height='64' alt='Untwyn logo' />}
				<DialogTitle sx={{fontSize: '2rem', p: 0}}>First time using Untwyn?</DialogTitle>
			</Box>
			<DialogContent>
				<Container maxWidth='md'>
					<Box mt={3} mb={2} textAlign='center'>
						<Typography variant='h6'>
						Which scenario best describes your workflow?
						</Typography>
					</Box>
					<Grid2 container spacing={2} sx={{mt: 2}}>
						{options.map((option) => (
							<Grid2 size={{xs: 12, sm: 12}} key={option.title}>
								<Card
									component='button'
									type='button'
									onClick={() => {
										setSelectedOption(option.title);
									}}
									disabled={option.isDisabled}
									sx={{
										...presetCardSx,
										border: selectedOption === option.title ? '2px solid' : '2px solid transparent',
										borderColor: selectedOption === option.title ? 'primary.main' : 'transparent',
										boxShadow: selectedOption === option.title ? 6 : presetCardSx.boxShadow,
									}}
								>
									<CardHeader sx={{pb: 0}} avatar={option.icon} />
									<CardContent sx={{pt: 1}}>
										<Typography variant='h6' mb={0.5}>{option.title}</Typography>
										<Typography variant='body2' color='text.secondary'>{option.description}</Typography>
									</CardContent>
								</Card>
							</Grid2>))}
					</Grid2>
					<Box component='form' onSubmit={handleContinue} mt={3}>
						<Box mt={1} display='flex' flexDirection='column' justifyContent='center' maxWidth={300} mx='auto' gap={1}>
							<Button
								fullWidth
								size='large'
								sx={{py: 1.5}}
								variant={selectedOption ? 'contained' : 'outlined'}
								type={canSubmitContinue ? 'submit' : 'button'}
								disabled={!selectedOption}
								color={selectedOptionConfig?.actionColor as any}
								onClick={
									selectedOption && needsCookieChoice
										? (e) => {
											e.preventDefault();
											requestCookiePromptFocus();
										}
										: undefined
								}
							>
								{!selectedOption
									? 'Let\'s go!'
									: needsCookieChoice
										? 'Review cookie preferences'
										: selectedOptionConfig?.actionLabel ?? 'Pick template'}
							</Button>
							<Button variant='text' size='small' onClick={handleCloseDialog}>
								Skip
							</Button>
						</Box>
					</Box>
				</Container>
			</DialogContent>
		</Box>
		<Box p={2} display='flex' justifyContent='center' gap={1}>
			<Link to='https://www.untwyn.com' target='_blank' rel='noopener noreferrer'>What is Untwyn?</Link>
			&nbsp;|&nbsp;
			<Link to='https://www.untwyn.com/docs' target='_blank' rel='noopener noreferrer'>Documentation</Link>
		</Box>
	</Dialog>;
};

export default IntroDialog;
